import { useState, useEffect, useRef } from 'react'

interface ScrollState {
  isScrollingDown: boolean
  isAtTop: boolean
}

const SCROLL_TOP_ENTER_THRESHOLD = 4
const SCROLL_TOP_EXIT_THRESHOLD = 24
const COMPACT_ENTER_THRESHOLD = 140

export const useScrollDirection = (): ScrollState => {
  const [isCompactHeader, setIsCompactHeader] = useState(false)
  const [isAtTop, setIsAtTop] = useState(true)
  const isAtTopRef = useRef(true)
  const isCompactHeaderRef = useRef(false)
  const ticking = useRef(false)

  useEffect(() => {
    const updateTopState = (currentScrollY: number) => {
      // Use hysteresis to avoid compact/expanded flicker near top.
      const atTop = isAtTopRef.current
        ? currentScrollY <= SCROLL_TOP_EXIT_THRESHOLD
        : currentScrollY <= SCROLL_TOP_ENTER_THRESHOLD

      if (atTop !== isAtTopRef.current) {
        isAtTopRef.current = atTop
        setIsAtTop(atTop)
      }

      return atTop
    }

    const updateCompactState = (currentScrollY: number) => {
      // Keep compact mode once entered and only reset when user returns to top.
      if (
        !isCompactHeaderRef.current &&
        currentScrollY > COMPACT_ENTER_THRESHOLD
      ) {
        isCompactHeaderRef.current = true
        setIsCompactHeader(true)
      }
    }

    const updateScrollState = () => {
      const currentScrollY = window.scrollY

      const atTop = updateTopState(currentScrollY)

      // If at top, always show header
      if (atTop) {
        if (isCompactHeaderRef.current) {
          isCompactHeaderRef.current = false
          setIsCompactHeader(false)
        }
        ticking.current = false
        return
      }

      updateCompactState(currentScrollY)

      ticking.current = false
    }

    const onScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(updateScrollState)
        ticking.current = true
      }
    }

    // Initialize
    const initialScrollY = window.scrollY
    const initialIsAtTop = initialScrollY <= SCROLL_TOP_EXIT_THRESHOLD
    const initialIsCompact = initialScrollY > COMPACT_ENTER_THRESHOLD

    isAtTopRef.current = initialIsAtTop
    isCompactHeaderRef.current = initialIsCompact

    setIsAtTop(initialIsAtTop)
    setIsCompactHeader(initialIsAtTop ? false : initialIsCompact)

    // Handle both scroll and touch events for mobile
    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, []) // Empty dependency array - only run once on mount

  return { isScrollingDown: isCompactHeader, isAtTop }
}
